import React, { useEffect, useState } from "react";
import { Eraser, Landmark, RefreshCw, Wrench } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { Panel } from "@/pages/shared";

// Eenmalige onderhoudsacties: plaatsstatussen gelijkzetten en testdata opruimen.
export default function AdminMaintenance() {
  const { t } = useLang();
  const [scan, setScan] = useState(null);
  const [ses, setSes] = useState(null);
  const [sesDone, setSesDone] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const [a, b] = await Promise.all([api.get("/admin/maintenance/scan"), api.get("/admin/maintenance/ses-scan")]);
      setScan(a.data); setSes(b.data.reservations); setSesDone(b.data.done);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const run = async (path, key, confirmKey) => {
    if (!window.confirm(t(confirmKey))) return;
    setBusy(key);
    try {
      const { data } = await api.post(`/admin/maintenance/${path}`);
      toast.success(`${t("saved")} · ${JSON.stringify(data.deleted ? { ...data.deleted, plaatsen: data.updated } : { plaatsen: data.updated })}`);
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const td = scan?.test_data;
  const tdCount = td ? td.guests.length + td.reservations.length + td.invoices.length + td.sales.length : 0;

  return (
    <div className="space-y-6">
      <Panel title={t("maint_pitches")} testid="maint-pitches-panel"
        action={<Button variant="ghost" size="sm" onClick={load} data-testid="maint-reload"><RefreshCw className="h-4 w-4" /></Button>}>
        <div className="p-5 space-y-3">
          <p className="text-sm text-muted-foreground">{t("maint_pitches_hint")}</p>
          <p className="text-sm font-semibold text-foreground" data-testid="maint-pitch-count">
            {t("maint_to_fix")}: {scan?.pitches.length ?? "…"}
          </p>
          {scan?.pitches.length > 0 && (
            <div className="max-h-52 overflow-y-auto cb-scroll rounded-lg border border-border p-3 text-xs space-y-0.5">
              {scan.pitches.map((p) => (
                <p key={p.label}><span className="font-semibold">{p.label}</span>: {p.from} → {p.to}{p.guest_name ? ` (${p.guest_name})` : ""}</p>
              ))}
            </div>
          )}
          <Button onClick={() => run("sync-pitches", "pitch", "maint_pitches_confirm")}
            disabled={busy === "pitch" || !scan?.pitches.length} data-testid="maint-sync-pitches">
            <Wrench className="h-4 w-4 mr-2" />{t("maint_sync_now")}
          </Button>
        </div>
      </Panel>

      <Panel title={t("maint_testdata")} testid="maint-testdata-panel">
        <div className="p-5 space-y-3">
          <p className="text-sm text-muted-foreground">{t("maint_testdata_hint")}</p>
          <p className="text-sm font-semibold text-foreground" data-testid="maint-testdata-count">
            {t("maint_found")}: {tdCount}
          </p>
          {td && tdCount > 0 && (
            <div className="max-h-52 overflow-y-auto cb-scroll rounded-lg border border-border p-3 text-xs space-y-0.5">
              {td.guests.map((g) => <p key={g.id}>{t("maint_guest")}: {g.name} {g.email}</p>)}
              {td.reservations.map((r) => <p key={r.id}>{t("maint_reservation")}: {r.guest_name} · {r.pitch_label} · {r.arrival} → {r.departure}</p>)}
              {td.invoices.map((i) => <p key={i.id}>{t("maint_invoice")}: {i.number} · {i.total}</p>)}
              {td.sales.map((s) => <p key={s.id}>{t("maint_sale")}: {s.number} · {s.total}</p>)}
            </div>
          )}
          <Button variant="destructive" onClick={() => run("cleanup-test-data", "clean", "maint_testdata_confirm")}
            disabled={busy === "clean" || !tdCount} data-testid="maint-cleanup">
            <Eraser className="h-4 w-4 mr-2" />{t("maint_cleanup_now")}
          </Button>
        </div>
      </Panel>

      <Panel title={t("maint_ses")} testid="maint-ses-panel">
        <div className="p-5 space-y-3">
          <p className="text-sm text-muted-foreground flex items-start gap-2"><Landmark className="h-4 w-4 mt-0.5 shrink-0" />{t("maint_ses_hint")}</p>
          <p className="text-sm font-semibold text-foreground" data-testid="maint-ses-count">{t("maint_found")}: {ses ? ses.length : "…"}</p>
          {ses?.length > 0 && (
            <div className="max-h-64 overflow-y-auto cb-scroll rounded-lg border border-border p-3 text-xs space-y-1.5">
              {ses.map((r) => (
                <div key={r.id} data-testid={`maint-ses-${r.id}`}>
                  <p className="font-semibold">{r.guest_name} · {r.pitch_label} · {r.arrival} → {r.departure} · {r.status}</p>
                  <p className="text-muted-foreground">{r.missing.join(" · ")}</p>
                </div>
              ))}
            </div>
          )}
          {sesDone ? (
            <p className="text-sm font-medium text-emerald-700" data-testid="maint-ses-done">{t("maint_ses_done")} {new Date(sesDone.at).toLocaleString()} · {sesDone.by} · {sesDone.deleted} / {sesDone.pitches}</p>
          ) : (
            <Button variant="destructive" onClick={() => run("ses-cleanup", "ses", "maint_ses_confirm")} disabled={busy === "ses" || !ses?.length} data-testid="maint-ses-cleanup">
              <Eraser className="h-4 w-4 mr-2" />{t("maint_ses_now")}
            </Button>
          )}
        </div>
      </Panel>
    </div>
  );
}
